export const SOF = 0x7e;

export const CMD = Object.freeze({
  // Reservados: el firmware todavía no implementa configuración remota.
  BEGIN_CONFIG: 0x01,
  WRITE_PARAM: 0x02,
  APPLY_CONFIG: 0x03,
  PING: 0x13,
  PROCESS_IMAGE: 0x14,
  IMAGE_RESULT: 0x15,
  READ_IMAGE_ROW: 0x16,
  READ_VARIABLE: 0x20,
  SLEEP: 0x30,
  STATUS: 0x31,
  RESET: 0x32,
  ACK: 0x80,
  NACK: 0x81,
  PONG: 0x85,
  TEST_WDT: 0xf0,
  TEST_STACK: 0xf1,
  TEST_TIMER0: 0xf2,
});

export const SUBSYS = Object.freeze({
  EPS: 0x01,
  TLM: 0x02,
  COMMS: 0x03,
  PAYLOAD: 0x04,
  MASTER: 0x05,
});

export const IMAGE_ALGORITHM = Object.freeze({
  THRESHOLD: 0x01,
  SOBEL: 0x02,
});

export const IMAGE_ALGORITHM_NAME = Object.freeze({
  [IMAGE_ALGORITHM.THRESHOLD]: "Umbral",
  [IMAGE_ALGORITHM.SOBEL]: "Sobel",
});

export const COMMAND_NAME = Object.freeze(
  Object.fromEntries(Object.entries(CMD).map(([name, code]) => [code, name]))
);

export const SUBSYSTEM_NAME = Object.freeze({
  [SUBSYS.EPS]: "EPS",
  [SUBSYS.COMMS]: "COMMS",
  [SUBSYS.TLM]: "TLM",
  [SUBSYS.PAYLOAD]: "PAYLOAD",
  [SUBSYS.MASTER]: "MASTER",
});


export function bytesToHex(bytes) {
  return Array.from(bytes || [])
    .map((value) => Number(value).toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase();
}

export function crc8(bytes) {
  let crc = 0x00;
  for (const byte of bytes) {
    crc ^= byte;
    for (let bit = 0; bit < 8; bit += 1) {
      crc = crc & 0x80 ? ((crc << 1) ^ 0x07) & 0xff : (crc << 1) & 0xff;
    }
  }
  return crc;
}

function decodeParamRecord(payload) {
  if (payload.length < 4) return null;
  const valueLength = payload[3];
  if (payload.length !== 4 + valueLength) return null;
  return {
    itemId: payload[0],
    paramId: payload[1],
    dtype: payload[2],
    valueLength,
    valueHex: bytesToHex(payload.slice(4)),
  };
}

function decodePayloadImageChunk(subsys, payload) {
  if (subsys !== SUBSYS.PAYLOAD || payload.length < 4) return null;

  const algorithm = payload[1];
  const chunkIndex = payload[2];
  const totalChunks = payload[3];
  if (!IMAGE_ALGORITHM_NAME[algorithm] || totalChunks < 1 || chunkIndex >= totalChunks) {
    return null;
  }

  return {
    requestSeq: payload[0],
    algorithm,
    algorithmName: IMAGE_ALGORITHM_NAME[algorithm],
    chunkIndex,
    totalChunks,
    pixelBytes: payload.length - 4,
    pixels: payload.slice(4),
    pixelsHex: bytesToHex(payload.slice(4)),
  };
}

export function describeFrame(frame) {
  const details = {};
  const payload = frame.payload || new Uint8Array();

  if (frame.cmd === CMD.ACK && payload.length >= 1) {
    details.requestCommand = payload[0];
    details.requestCommandName = COMMAND_NAME[payload[0]] || "UNKNOWN";
  } else if (frame.cmd === CMD.NACK && payload.length >= 1) {
    details.requestCommand = payload[0];
    details.requestCommandName = COMMAND_NAME[payload[0]] || "UNKNOWN";
    details.errorCode = payload[1] ?? null;
  } else if (frame.cmd === CMD.STATUS) {
    details.statusBytes = Array.from(payload);
    details.statusLengthValid = payload.length === 8;
  } else if (frame.cmd === CMD.READ_VARIABLE) {
    details.variableId = payload[0] ?? null;
    if (payload.length >= 3) {
      details.value = (payload[1] << 8) | payload[2];
      details.valueHigh = payload[1];
      details.valueLow = payload[2];
    }
  } else if (frame.cmd === CMD.IMAGE_RESULT) {
    details.imageChunk = decodePayloadImageChunk(frame.subsys, payload);
  } else if (frame.cmd === CMD.WRITE_PARAM) {
    details.record = decodeParamRecord(payload);
  }

  return {
    ...frame,
    commandName: COMMAND_NAME[frame.cmd] || "UNKNOWN",
    subsystemName: SUBSYSTEM_NAME[frame.subsys] || "UNKNOWN",
    details,
  };
}

function hexToBytes(hex = "") {
  const cleanHex = String(hex).replace(/0x/gi, "").replace(/[^a-fA-F0-9]/g, "").toUpperCase();
  if (!cleanHex) return { cleanHex, bytes: new Uint8Array(), error: "No hay bytes" };
  if (cleanHex.length % 2) {
    return { cleanHex, bytes: new Uint8Array(), error: "HEX con cantidad impar de dígitos" };
  }
  const bytes = new Uint8Array(cleanHex.length / 2);
  for (let index = 0; index < cleanHex.length; index += 2) {
    bytes[index / 2] = Number.parseInt(cleanHex.slice(index, index + 2), 16);
  }
  return { cleanHex, bytes, error: null };
}

function field(offset, name, value, meaning, length = 1) {
  return {
    offset: length <= 1 ? String(offset) : `${offset}-${offset + length - 1}`,
    name,
    value,
    meaning,
  };
}

export function inspectFrameHex(hex) {
  const decoded = hexToBytes(hex);
  const { cleanHex, bytes } = decoded;
  if (decoded.error) {
    return { cleanHex, valid: false, crcValid: false, lengthValid: false, error: decoded.error, fields: [] };
  }
  if (bytes.length < 6) {
    return { cleanHex, valid: false, crcValid: false, lengthValid: false, error: "Trama incompleta", fields: [] };
  }

  const payloadLength = bytes[4];
  const expectedLength = payloadLength + 6;
  const lengthValid = bytes.length === expectedLength;
  const crcIndex = 5 + payloadLength;
  const receivedCrc = bytes[crcIndex];
  const expectedCrc = lengthValid ? crc8(bytes.slice(1, crcIndex)) : null;
  const crcValid = lengthValid && receivedCrc === expectedCrc;
  const payload = bytes.slice(5, Math.min(crcIndex, bytes.length));
  const commandName = COMMAND_NAME[bytes[2]] || "DESCONOCIDO";
  const subsystemName = SUBSYSTEM_NAME[bytes[1]] || "DESCONOCIDO";
  const fields = [
    field(0, "SOF", bytesToHex(bytes.slice(0, 1)), "Inicio fijo de trama: 0x7E"),
    field(1, "SUB", bytesToHex(bytes.slice(1, 2)), `${subsystemName}: destino/origen lógico usado por la GUI`),
    field(2, "CMD", bytesToHex(bytes.slice(2, 3)), `${commandName}: operación solicitada o respuesta`),
    field(3, "SEQ", bytesToHex(bytes.slice(3, 4)), `Secuencia ${bytes[3]} para correlacionar solicitud y respuesta`),
    field(4, "LEN", bytesToHex(bytes.slice(4, 5)), `${payloadLength} byte(s) de payload`),
  ];

  if (payload.length) {
    if (bytes[2] === CMD.WRITE_PARAM && payload.length >= 4) {
      fields.push(field(5, "PAYLOAD.ITEM_ID", bytesToHex(payload.slice(0, 1)), `Sensor o variable ${payload[0]}`));
      fields.push(field(6, "PAYLOAD.PARAM_ID", bytesToHex(payload.slice(1, 2)), `Parámetro ${payload[1]}`));
      fields.push(field(7, "PAYLOAD.TYPE", bytesToHex(payload.slice(2, 3)), `Código de tipo ${payload[2]}`));
      fields.push(field(8, "PAYLOAD.VALUE_LEN", bytesToHex(payload.slice(3, 4)), `${payload[3]} byte(s) de valor`));
      if (payload.length > 4) fields.push(field(9, "PAYLOAD.VALUE", bytesToHex(payload.slice(4)), "Valor codificado", payload.length - 4));
    } else if (bytes[2] === CMD.READ_VARIABLE) {
      fields.push(field(5, "PAYLOAD.VARIABLE_ID", bytesToHex(payload.slice(0, 1)), `Variable ${payload[0]}`));
      if (payload.length >= 3) fields.push(field(6, "PAYLOAD.VALUE_U16_BE", bytesToHex(payload.slice(1, 3)), `Valor ${(payload[1] << 8) | payload[2]}`, 2));
      if (payload.length > 3) fields.push(field(8, "PAYLOAD.EXTRA", bytesToHex(payload.slice(3)), "Bytes adicionales definidos por firmware", payload.length - 3));
    } else if (bytes[2] === CMD.ACK || bytes[2] === CMD.NACK) {
      fields.push(field(5, "PAYLOAD.REQUEST_CMD", bytesToHex(payload.slice(0, 1)), COMMAND_NAME[payload[0]] || `Comando 0x${bytesToHex(payload.slice(0, 1))}`));
      if (bytes[2] === CMD.NACK && payload.length >= 2) fields.push(field(6, "PAYLOAD.ERROR", bytesToHex(payload.slice(1, 2)), `Código de error ${payload[1]}`));
      if (payload.length > 2) fields.push(field(7, "PAYLOAD.EXTRA", bytesToHex(payload.slice(2)), "Bytes adicionales definidos por firmware", payload.length - 2));
    } else {
      fields.push(field(5, "PAYLOAD", bytesToHex(payload), "Datos específicos del comando", payload.length));
    }
  } else {
    fields.push(field(5, "PAYLOAD", "—", "Vacío (LEN = 0)", 0));
  }

  if (receivedCrc !== undefined) {
    fields.push(field(crcIndex, "CRC8", bytesToHex(bytes.slice(crcIndex, crcIndex + 1)), crcValid
      ? "Correcto; calculado sobre SUB | CMD | SEQ | LEN | PAYLOAD"
      : `Incorrecto; se esperaba ${expectedCrc === null ? "longitud válida" : `0x${expectedCrc.toString(16).padStart(2, "0").toUpperCase()}`}`));
  }

  let parsed = null;
  let error = null;
  if (bytes[0] !== SOF) error = "SOF inválido";
  else if (!lengthValid) error = `Longitud inválida: LEN anuncia ${expectedLength} bytes totales y llegaron ${bytes.length}`;
  else if (!crcValid) error = "CRC8 inválido";
  else {
    try {
      parsed = parseFrame(bytes);
    } catch (parseError) {
      error = parseError.message;
    }
  }

  return {
    cleanHex,
    bytes: Array.from(bytes),
    valid: !error,
    lengthValid,
    crcValid,
    expectedCrc,
    receivedCrc,
    parsed,
    error,
    fields,
  };
}

export function parseFrame(input) {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input);
  if (bytes.length < 6) throw new Error("Trama incompleta");
  if (bytes[0] !== SOF) throw new Error("SOF inválido");

  const len = bytes[4];
  const expectedLength = 6 + len;
  if (bytes.length !== expectedLength) {
    throw new Error(`Longitud inválida: esperados ${expectedLength}, recibidos ${bytes.length}`);
  }

  const receivedCrc = bytes[5 + len];
  const expectedCrc = crc8(bytes.slice(1, 5 + len));
  if (receivedCrc !== expectedCrc) {
    throw new Error(
      `CRC8 inválido: recibido 0x${receivedCrc.toString(16).padStart(2, "0")}, esperado 0x${expectedCrc
        .toString(16)
        .padStart(2, "0")}`
    );
  }

  return describeFrame({
    subsys: bytes[1],
    cmd: bytes[2],
    seq: bytes[3],
    len,
    payload: bytes.slice(5, 5 + len),
    payloadHex: bytesToHex(bytes.slice(5, 5 + len)),
    crc: receivedCrc,
    frameHex: bytesToHex(bytes),
  });
}

export class FrameStreamDecoder {
  constructor() {
    this.buffer = new Uint8Array();
  }

  reset() {
    this.buffer = new Uint8Array();
  }

  push(chunk) {
    const incoming = chunk instanceof Uint8Array ? chunk : new Uint8Array(chunk);
    const merged = new Uint8Array(this.buffer.length + incoming.length);
    merged.set(this.buffer, 0);
    merged.set(incoming, this.buffer.length);
    this.buffer = merged;

    const frames = [];
    const errors = [];

    while (this.buffer.length) {
      const sofIndex = this.buffer.indexOf(SOF);
      if (sofIndex < 0) {
        errors.push({ error: "Datos descartados sin SOF", discardedHex: bytesToHex(this.buffer) });
        this.buffer = new Uint8Array();
        break;
      }
      if (sofIndex > 0) {
        errors.push({
          error: "Datos descartados antes de SOF",
          discardedHex: bytesToHex(this.buffer.slice(0, sofIndex)),
        });
        this.buffer = this.buffer.slice(sofIndex);
      }
      if (this.buffer.length < 5) break;

      const totalLength = 6 + this.buffer[4];
      if (this.buffer.length < totalLength) break;

      const candidate = this.buffer.slice(0, totalLength);
      this.buffer = this.buffer.slice(totalLength);
      try {
        frames.push(parseFrame(candidate));
      } catch (error) {
        errors.push({ error: error.message, frameHex: bytesToHex(candidate) });
      }
    }

    return { frames, errors };
  }
}
